/**
 * The 18 software-sprite slots (02 §4 Capacity) and the bookkeeping every bubble routine shares: slot
 * order, making and freeing bubbles, the column the C64 derives from X, the collision map, and the
 * contract fields (`phase`, `visible`) kept in step with the C64 type.
 */

import { solidTile } from '../playfield';
import { GONE, GONE2, HIDDEN, JUST_BLOWN, NOT_BUBBLE, isTrappedCode } from './codes';
import { initialBubbleMotion, type BubbleMotion, type FoodFall } from './state';
import { SLOTS } from './tuning';
import type { Bubble, BubbleContent, Item, PlayerId, World } from '../types';

/** A software sprite's column [$DC,x] from its X: (X - $14) / 8. */
export const colOfX = (x: number): number => ((x - 0x14) & 0xff) >> 3;
/** The bubble's column [$DC,x]: (X - $14) / 8. */
export const colOf = (b: Bubble): number => colOfX(b.x);
/** The bubble's 2-px step into its column [$A9C4,x]. */
export const subXOf = (b: Bubble): number => (((b.x - 0x14) & 0xff) >> 1) & 3;

/**
 * Is the cell at tile (col, row) solid in the C64's collision map at $8500? Rows above the top and
 * below the bottom copy the edge rows (D_3A6C); columns outside the grid are empty.
 */
export function mapSolid(world: World, col: number, row: number): boolean {
  return solidTile(world.playfield, col, Math.max(0, Math.min(24, row)));
}

/** The bubbles in the C64's loop order, slot 17 first (a copy: the loops may free bubbles). */
export const bySlot = (world: World): Bubble[] => world.bubbles.slice();

/** Where the C64 draws a software sprite from its row and sub-row (a non-zero sub-row: in the row above). */
export const drawnY = (row: number, subY: number): number => (row * 8 + 0x15 + (subY !== 0 ? subY - 8 : 0)) & 0xff;

/** A bubble-food item's C64 cell and slot (type $4C), or null for any other item. */
export function foodFallOf(item: Item): FoodFall | null {
  return item.kind === 'bubbleFood' ? (item as Partial<FoodItem>).fall ?? null : null;
}

export type FoodItem = Item & { fall: FoodFall };

/**
 * The slots the stream's other software sprites hold: fire drops, bolts, bubble food. Read without
 * making the system (that would roll the letters' coin early).
 */
function specialSlots(world: World): number[] {
  const sys = world.bubbleSystem;
  const out: number[] = [];
  for (const it of world.items) {
    const f = foodFallOf(it);
    if (f) out.push(f.slot);
  }
  if (!sys) return out;
  for (const d of sys.drops) out.push(d.slot);
  for (const b of sys.bolts) out.push(b.slot);
  return out;
}

/** Slots in use: every bubble until its slot is freed, drops, bolts, bubble food, the projectiles, the Barons. */
export const slotsUsed = (world: World): number =>
  world.bubbles.length + specialSlots(world).length + world.projectiles.length + world.barons.length;

/**
 * The slot a new bubble takes, or -1: the C64 takes the highest free one (L2321, L_0C6B). Which slots
 * the projectiles and Barons hold is not known here, so bubbles take the highest this stream leaves.
 */
export function freeSlot(world: World): number {
  if (slotsUsed(world) >= SLOTS) return -1;
  const used = new Set([...world.bubbles.map((b) => b.motion.slot), ...specialSlots(world)]);
  for (let s = SLOTS - 1; s >= 0; s--) if (!used.has(s)) return s;
  return -1;
}

/**
 * A drop or bolt ends in its slot as a bubble-slot type again: $38 to vanish over two passes (landed,
 * run off the screen), or $3A to burst without points (the level-clear sweep, a bolt in the boss).
 * `saved` is the type it had, which the burst passes on (routine_3D77: none of the bubbles' effects).
 */
export function endInSlot(
  world: World, slot: number, at: { x: number; y: number; row: number; subY: number }, code: number, saved: number,
): Bubble {
  return addBubble(world, slot, {
    x: at.x, y: at.y, owner: -1, content: { kind: 'empty' },
    motion: { code, saved, row: at.row, subY: at.subY, burst: -1 },
  });
}

export interface NewBubble {
  x: number;
  y: number;
  owner: PlayerId | -1;
  content: BubbleContent;
  motion: Partial<BubbleMotion>;
}

/** Make a bubble in `slot`, keeping `world.bubbles` in slot order (17 first). */
export function addBubble(world: World, slot: number, init: NewBubble): Bubble {
  const b: Bubble = {
    id: world.nextId++, x: init.x & 0xff, y: init.y & 0xff, owner: init.owner, content: init.content,
    phase: 'float', visible: true, motion: { ...initialBubbleMotion(), ...init.motion, slot },
  };
  syncView(b);
  const at = world.bubbles.findIndex((o) => o.motion.slot < slot);
  if (at < 0) world.bubbles.push(b);
  else world.bubbles.splice(at, 0, b);
  return b;
}

/** Free a bubble's slot (the C64's type $FF, D_7BDB). */
export function freeBubble(world: World, b: Bubble): void {
  const i = world.bubbles.indexOf(b);
  if (i >= 0) world.bubbles.splice(i, 1);
}

/** Set the C64 type and bring the contract fields along. */
export function setCode(b: Bubble, code: number): void {
  b.motion.code = code;
  syncView(b);
}

/**
 * `phase` and `visible` from the C64 state: a shot until its steps run out; bursting or vanishing once
 * the type is $34 or more (the blink's invisible half, $48, still floats); invisible while blinked or
 * flashed off, and once vanished.
 */
export function syncView(b: Bubble): void {
  const m = b.motion;
  const c = m.code;
  const floating = c < NOT_BUBBLE || c === HIDDEN;
  if (m.burst >= 0 || !floating) b.phase = 'pop';
  else b.phase = c === JUST_BLOWN || m.steps !== 0 ? 'shot' : 'float';
  if (m.burst >= 0) b.visible = m.burst < 4;
  else if (c === HIDDEN || c === GONE || c === GONE2) b.visible = false;
  else b.visible = !(isTrappedCode(c) && m.flashOff);
}
